import { ListHeading } from "@/components/shared/list-header";
import { Input, View, Textarea } from "@/components/ui";
import { ClipboardText } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import { InputFormControl } from "@/components/shared/input-form-control";
import {
  type Control,
  Controller,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { type ElderCareFormData } from "./useElderCareForm";
import { SelectInputFormControl } from "@/components/shared/select-input-form-control";
import { Divider } from "@/components/shared/divider";
import { ListCheckboxInputItem } from "@/components/shared/list-checkbox-input";
import {
  ageOptions,
  mobilityStatus,
  mobilityStatusOptions,
  requiredAssistancesOptions,
  symptomOptions,
  weightOptions,
} from "./lib";
import { SalesModal } from "@/components/shared/booking/sales-modal";
import { getSalesModalDescription } from "@/containers/booking/services/elder-care-booking/helper";
import { useEffect, useState } from "react";
import { IconButton } from "@/components/ui/icon-button";

export const ElderCareForm = ({
  control,
}: {
  control: Control<ElderCareFormData>;
}) => {
  const [openChatSales, setOpenChatSales] = useState<boolean>(false);

  const handleSalesModalClose = (isOpen: boolean) => {
    setOpenChatSales(isOpen);
  };

  const mobilityStatusValue = useWatch({
    control,
    name: "mobility_status",
  });
  const weight = useWatch({
    control,
    name: "weight",
  });

  useEffect(() => {
    if (mobilityStatusValue && weight) {
      const weightAbove50 = parseInt(weight) > 50;

      const isMobilityHasBedriddenOrWheels = mobilityStatus
        .slice(2, 4)
        .some((option) => option === mobilityStatusValue);

      if (weightAbove50 && isMobilityHasBedriddenOrWheels) {
        setOpenChatSales(true);
      }
    }
  }, [mobilityStatusValue, weight]);

  return (
    <View>
      <ListHeading title="Pre-Service Assessment" icon={ClipboardText} />
      <Typography
        color="foreground-intermediate"
        variant="body-md"
        className="pt-1"
      >
        Helps us assess your needs and assign the right care aide.
      </Typography>

      <View className="pt-4">
        <InputFormControl
          name="name"
          label="Save this profile as:"
          component={Input}
          control={control}
          componentProps={{
            placeholder: "Care Recipient's Name",
          }}
        />
        <View className="flex flex-row gap-2">
          <View className="flex-1">
            <SelectInputFormControl
              label="Care Recipient's Age:"
              name="age"
              control={control}
              options={ageOptions}
              fullWidth
            />
          </View>
          <View className="flex-1">
            <ElderWeightInput control={control} />
          </View>
        </View>
        <SelectInputFormControl
          label="Care Recipient's Mobility Status:"
          name="mobility_status"
          control={control}
          options={mobilityStatusOptions}
          fullWidth
        />
        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />
        <ElderSymptomListCheckbox
          onSymptomChange={() => setOpenChatSales(true)}
        />
        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />
        <RequiredAssistanceListCheckbox control={control} />
        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />
        <InputFormControl
          label="Tell us a little more about the care recipient's condition:"
          name="additional_notes"
          control={control}
          component={Textarea}
          componentProps={{
            placeholder:
              "e.g. Dementia, mobility issues, requires help with meals and bathing",
          }}
        />
      </View>
      <SalesModal
        open={openChatSales}
        setOpen={handleSalesModalClose}
        title="Need help with your Elder requirements?"
        description={getSalesModalDescription}
      />
    </View>
  );
};

const ElderWeightInput = ({
  control,
}: {
  control: Control<ElderCareFormData>;
}) => {
  const [isOthers, setIsOthers] = useState(false);
  const weight = useWatch({
    control,
    name: "weight",
  });

  const form = useFormContext();

  useEffect(() => {
    if (weight === "others") {
      setIsOthers(true);
    }
  }, [weight]);

  if (isOthers) {
    return (
      <InputFormControl
        label="Weight (Kg):"
        name="weight"
        component={Input}
        control={control}
        componentProps={{
          placeholder: "Enter weight",
          type: "number",
        }}
        rightRender={
          <IconButton
            size="sm"
            variant="ghost"
            color="foreground"
            className="absolute right-1 top-1"
            iconName="x"
            onClick={() => {
              form.setValue("weight", "");
              setIsOthers(false);
            }}
          />
        }
      />
    );
  }

  return (
    <SelectInputFormControl
      label="Weight (Kg):"
      name="weight"
      control={control}
      options={[...weightOptions, { value: "others", label: "Others" }]}
      fullWidth
    />
  );
};

/**
 * Symptom checklist component with independent state (not sent to form/backend).
 * Triggers callback when symptoms are selected to show sales modal.
 */
export const ElderSymptomListCheckbox = ({
  onSymptomChange,
}: {
  onSymptomChange: () => void;
}) => {
  const items = symptomOptions;
  const [selectedItems, setSelectedItems] = useState<boolean[]>(
    new Array(items.length).fill(false),
  );

  const allSelected =
    selectedItems.length > 0 && selectedItems.every((item) => item);

  const handleSelectAll = () => {
    const newValue = !allSelected;
    setSelectedItems(new Array(items.length).fill(newValue));
    if (newValue) {
      onSymptomChange();
    }
  };

  const handleItemChange = (index: number, value: boolean) => {
    const newSelectedItems = [...selectedItems];
    newSelectedItems[index] = value;
    setSelectedItems(newSelectedItems);
    if (value) {
      onSymptomChange();
    }
  };

  return (
    <View className="pb-3">
      <Typography variant="label-sm" className="block pb-4">
        Does the care recipient display any of the following symptoms?
      </Typography>

      <ListCheckboxInputItem
        checked={allSelected}
        onChange={handleSelectAll}
        label="Select All"
      />

      {items.map((item, index) => (
        <ListCheckboxInputItem
          key={item.value}
          checked={selectedItems[index]}
          onChange={(_, value) => handleItemChange(index, value)}
          label={item.label}
        />
      ))}
    </View>
  );
};

const RequiredAssistanceListCheckbox = ({
  control,
  disabled = false,
}: {
  control: Control<ElderCareFormData>;
  disabled?: boolean;
}) => {
  const name = "required_assistances";
  const items = requiredAssistancesOptions;
  const field = useWatch({
    control,
    name,
  });
  const form = useFormContext();

  const allSelected = field.every((item) => item.selected);

  return (
    <View className="pb-3">
      <Typography variant="label-sm" className="block pb-4">
        What assistance does the care recipient require from our care aides?
      </Typography>

      <ListCheckboxInputItem
        checked={allSelected}
        onChange={() => {
          form.setValue(
            name,
            field.map((item) => ({ ...item, selected: !allSelected })),
          );
        }}
        label="Select All"
      />

      {items.map((item, index) => (
        <Controller
          key={item.value}
          control={control}
          name={`${name}.${index}.selected`}
          render={({ field }) => (
            <ListCheckboxInputItem
              key={item.value}
              checked={field.value}
              onChange={(_, value) => {
                field.onChange(value);
              }}
              label={item.label}
              disabled={disabled}
            />
          )}
        />
      ))}
    </View>
  );
};
